import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button, Input } from '@/features/common/ui';
import { AuthShell } from './LoginPage';

/**
 * Asking for a reset code.
 *
 * The login page has linked here since it was written; the route did not exist,
 * so "Forgot password?" was a dead link to a redirect back to the dashboard.
 *
 * The answer is deliberately the same whether or not the address is registered
 * - the API returns one sentence either way - so this screen must not imply
 * otherwise. "If that email has an account" is doing real work: worded as
 * "we have sent you a code", a stranger typing addresses learns which ones are
 * customers here.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const request = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/auth/password/forgot', { email }),
    // Straight to the code screen, carrying the address so nobody types it
    // twice. It is in the URL rather than component state because a person
    // reads their email, comes back, and may well have reloaded the tab.
    onSuccess: () => navigate(`/reset-password?email=${encodeURIComponent(email)}`),
  });

  const error = request.error instanceof ApiError ? request.error : undefined;

  return (
    <AuthShell
      title="Forgot your password?"
      subtitle="We will send a six-digit code to your email address."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          request.mutate();
        }}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error?.fieldError('email')}
        />

        {error && !error.errors?.length ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}

        <Button type="submit" className="w-full" loading={request.isPending}>
          Send me a code
        </Button>
      </form>

      <div className="mt-4 flex items-center justify-between text-sm">
        <Link to="/login" className="text-muted-foreground hover:text-foreground">
          Back to sign in
        </Link>
        <Link to="/reset-password" className="font-medium text-primary hover:underline">
          I already have a code
        </Link>
      </div>
    </AuthShell>
  );
}

/**
 * Entering the code and a new password.
 *
 * Reachable directly, not only after the screen above, because two things land
 * people here with a code already in hand: an invitation to a book
 * (which mints one so a new member can set their first password), and a person
 * who closed the tab before reading their email.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  // An invitation link carries the code as well, so the person lands with
  // both fields filled and only the password to choose.
  const [otp, setOtp] = useState(params.get('code') ?? '');
  const [password, setPassword] = useState('');

  const reset = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/auth/password/reset', { email, otp, password }),
    // Not signed in afterwards, on purpose. A reset revokes every session the
    // account had, and quietly opening a new one here would undo half of what
    // that is for - the person proves the new password immediately instead.
    onSuccess: () => navigate('/login?reset=1'),
  });

  const error = reset.error instanceof ApiError ? reset.error : undefined;

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Enter the code from your email, and the password you want to use."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate();
        }}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error?.fieldError('email')}
        />
        <Input
          label="Six-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          error={error?.fieldError('otp')}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error?.fieldError('password')}
        />

        {error && !error.errors?.length ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}

        <Button type="submit" className="w-full" loading={reset.isPending}>
          Set password and sign in
        </Button>
      </form>

      <p className="mt-4 text-center text-sm">
        <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
          Send me another code
        </Link>
      </p>
    </AuthShell>
  );
}
